// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2025-02-20 14:39:46 +0100 (Thu, 20 Feb 2025) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: o.powroznik $
// Revision    : $Rev: 150357 $
// Purpose     : render notifications
// =============================================================================

// ---Third-party imports
import React, {Component, useState, useEffect} from 'react'
import {connect} from 'react-redux'
import {Message, Transition} from 'semantic-ui-react'
import clsx from 'clsx'
import {isString} from 'ldash'

// ---Framework imports
import {Portal, VIcon} from 'uix/components'
import log from 'uix/logging'
import {ucFirst} from 'uix/utils'

// ---Local imports
import S from './notify.scss'
import {dismissNotification} from './actions'

// ---Globals
const Notification = ({
  message,
  header,
  type,
  color, // overriding type colors
  details,
  position = 'top', // top, bottom, top left, bottom right...
  id,
  icon,
  onCloseClick,
  visible,
}) => {
  let [cVisible, setCVisible] = useState(false)

  useEffect(() => {
    // needed for missing entry transition
    setCVisible(visible)
  }, [visible])

  let headerText = header
  let messageText = message

  // better styling for positive cases
  let colorMapping = {
    SUCCESS: 'green',
    POSITIVE: 'green',
  }
  let cColor = (!color && colorMapping[type]) || undefined

  // Set position of toast
  let verticalStyle = position.includes('top') ? {top: 0} : {bottom: 0}
  let horizontalStyle = position.includes('left')
    ? {justifyContent: 'flex-start'}
    : position.includes('right')
    ? {justifyContent: 'flex-end'}
    : {}

  // Set animation based on position
  let animation = position.includes('top') ? 'fade down' : 'fade up'
  animation = position.includes('left')
    ? 'fade right'
    : position.includes('right')
    ? 'fade left'
    : animation

  if (!headerText) {
    headerText = message
    messageText = null
  }

  // Turn the type string into a boolean prop with the same name
  const typeObj = !color && {[type.toLowerCase()]: true}

  if (messageText) {
    const messagePieces = messageText.split('\n')
    messageText = messagePieces.map((piece, i) => <div key={i}>{piece}</div>)
  } else if (!headerText) {
    headerText = ucFirst(type.toLowerCase())
  }

  return (
    <div className={S.toaster} style={{...verticalStyle, ...horizontalStyle}}>
      <Transition visible={cVisible} animation={animation} duration={700}>
        <Message
          floating
          className={clsx(S.toast, isString(icon) && S.icon, onCloseClick && S.close)}
          size="tiny"
          color={cColor}
          icon={isString(icon)}
          onDismiss={() => onCloseClick && onCloseClick(id)}
          {...typeObj}
        >
          {icon && <VIcon name={icon} />}
          <Message.Content>
            {headerText && <Message.Header>{headerText}</Message.Header>}
            {messageText}
            {details ? <pre style={{fontSize: '0.7em', lineHeight: '1.0em'}}>{details}</pre> : null}
          </Message.Content>
        </Message>
      </Transition>
    </div>
  )
}

const actions = {dismissNotification}

const mapState = root => ({
  notifications: root.data.notify,
})

export class NotificationManager extends Component {
  render() {
    let {notifications, dismissNotification} = this.props
    let renderedNotifications = notifications.map(n => (
      <Notification key={n.id} onCloseClick={dismissNotification} {...n} />
    ))

    return <Portal>{renderedNotifications}</Portal>
  }
}

export default connect(
  mapState,
  actions
)(NotificationManager)
